/**
 * Speaker profile card (community-speech plan, Phase B).
 *
 * Opt-in demographics that make speech corpora scientifically useful —
 * age band, gender, language varieties. Consent-first: every field is
 * optional, and NOTHING is attached to exported recordings unless the
 * explicit sharing consent below is ticked (versioned + audit-logged
 * server-side; revocable any time).
 */
import { useCallback, useEffect, useState } from "react";
import { Button, Checkbox, SettingsCard, useToast } from "@humansignal/ui";
import { Input, Select } from "../../components/Form/Elements";
import { useTranslation } from "../../i18n/LocaleProvider";

const getCsrfToken = () => {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
};

const AGE_BANDS = ["", "teens", "twenties", "thirties", "forties", "fifties", "sixties", "seventies_plus"];
const GENDERS = ["", "female", "male", "non_binary", "prefer_not_to_say", "other"];

export const UserProfileSpeaker = ({ onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [ageBand, setAgeBand] = useState("");
  const [gender, setGender] = useState("");
  const [varieties, setVarieties] = useState("");
  const [consent, setConsent] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch("/api/current-user/speaker-profile/", {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!r.ok) return;
      const b = await r.json();
      setAgeBand(b.age_band || "");
      setGender(b.gender || "");
      setVarieties((b.varieties || []).join(", "));
      setConsent(!!b.share_consent);
      setLoaded(true);
    } catch {
      /* card stays hidden on failure */
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch("/api/current-user/speaker-profile/", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken(),
        },
        body: JSON.stringify({
          age_band: ageBand,
          gender,
          varieties: varieties
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean),
          share_consent: consent,
        }),
      });
      if (r.ok) {
        toast.show({ message: t("pages.userprofile.speaker.saved_toast") });
        onSaved?.();
      } else {
        const b = await r.json().catch(() => ({}));
        toast.show({ type: "error", message: b.detail || `(${r.status})` });
      }
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <SettingsCard title={t("pages.userprofile.speaker.title")} description={t("pages.userprofile.speaker.body")}>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <Select
            label={t("pages.userprofile.speaker.age_band")}
            value={ageBand}
            options={AGE_BANDS.map((k) => ({
              value: k,
              label: k ? t(`pages.userprofile.speaker.ages.${k}`) : "—",
            }))}
            onChange={(v) => setAgeBand(v ?? "")}
          />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <Select
            label={t("pages.userprofile.speaker.gender")}
            value={gender}
            options={GENDERS.map((k) => ({
              value: k,
              label: k ? t(`pages.userprofile.speaker.genders.${k}`) : "—",
            }))}
            onChange={(v) => setGender(v ?? "")}
          />
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <Input
          label={t("pages.userprofile.speaker.varieties")}
          value={varieties}
          onChange={(e) => setVarieties(e.target.value)}
          placeholder={t("pages.userprofile.speaker.varieties_placeholder")}
        />
      </div>
      <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginBottom: 16 }}>
        {t("pages.userprofile.speaker.consent_label")}
      </Checkbox>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="button"
          size="medium"
          waiting={saving}
          onClick={save}
          aria-label={t("pages.userprofile.speaker.save")}
        >
          {t("pages.userprofile.speaker.save")}
        </Button>
      </div>
    </SettingsCard>
  );
};
